import { useNavigateToSearch } from '@components/SearchV2/hooks/useSearchQueryUrl';
import { useCurrency } from '@contexts/CurrencyContext';
import { useProductLists } from '@contexts/ProductListsContext';
import { analytics } from '@lib/analytics/page-events';
import { GENESIS_PROVIDER_ID } from '@lib/api/auth/genesis-provider';
import { useEnsureBridgeToken } from '@lib/commerce/useBridgeToken';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { failingFetcher } from '@lib/http/fetch';
import { FetchError } from '@lib/types/api/FetchError';
import { ReviewsData } from '@lib/types/api/Review';
import { buildManageSeatUrl, openManageSeatUrl } from '@lib/util/manageSeat';
import { buildRequestSeatUrl, openRequestSeatUrl } from '@lib/util/requestSeat';
import { openInUnityEditor } from '@lib/util/unityEditor';
import * as Sentry from '@sentry/nextjs';
import { REVIEWS_SHEET_INDEX } from '@ui-components/components/organisms/ProductTabs/ProductTabs';
import { LineItemType } from '@unity/commerce-bridge';
import { useCartManagement } from '@unity/commerce-core';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ANALYTICS_POLLING_INTERVAL_MS,
  MAX_ANALYTICS_RETRIES,
} from 'src/app/constants/analytics';
import {
  PackageListDetailPropsData,
  ProductReviewFilterValues,
  ProductTabValue,
  ReviewFormData,
  WriteReviewModalPropsData,
  useBreakpoints,
} from 'ui-components';
import { formatDate } from 'utils';
import { MAX_REVIEW_STARS } from '../../constants/reviews';
import { useReviews } from '../../hooks/useReviews';
import { buildCheckoutUrl as buildCheckoutUrlPure } from './buildCheckoutUrl';
import {
  UpmProductDetailPagePropsActions,
  UpmProductDetailPagePropsData,
  UseUpmProductDetailPageProps,
} from './types';

export const useUpmProductDetailPage = (
  initialData: UpmProductDetailPagePropsData
): UseUpmProductDetailPageProps => {
  const router = useRouter();
  const breakpoints = useBreakpoints();
  const navigateToSearch = useNavigateToSearch();

  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const isPaidUpmEnabled = flags?.[Flags.PAID_UPM]?.value === FEATURE_FLAG_ON;
  const { hasCookieConsent, openCookieSettings } = useCookieSettings();
  const { isProductInList, toggleProductList, fetchProductMembership } =
    useProductLists();
  const { data: session } = useSession();
  const {
    cart,
    env,
    organizationId,
    store,
    replaceLineItemsInCart,
    setEnableCartFetching,
    setCurrencyCode,
  } = useCartManagement();
  const { currency } = useCurrency();
  const ensureBridgeToken = useEnsureBridgeToken();

  // Keep the commerce cart's currency in sync with the footer/header selector;
  // the provider only reads currencyCode once at mount otherwise.
  useEffect(() => {
    setCurrencyCode(currency);
  }, [currency, setCurrencyCode]);
  const [data, setData] = useState(initialData);

  // The footer currency selector drives useCurrency(); selecting the displayed
  // price from initialData.priceByCurrency here keeps the PDP reactive on the
  // client, like search/listing/homepage.
  const displayedPrice =
    initialData.priceByCurrency?.[currency] ?? initialData.productInfo.price;
  const {
    currentPrice,
    originalPrice,
    currency: priceCurrency,
    discountPercent,
  } = displayedPrice;

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      productInfo: {
        ...prev.productInfo,
        price: {
          currentPrice,
          originalPrice,
          currency: priceCurrency,
          discountPercent,
        },
      },
    }));
  }, [currentPrice, originalPrice, priceCurrency, discountPercent]);

  const [shouldScrollToTab, setShouldScrollToTab] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  // tracked apart from showServerError, which a transient success clears
  const [entitlementErrorDismissed, setEntitlementErrorDismissed] =
    useState(false);
  // only when the failure actually collapsed the CTA — registry products keep theirs
  const showEntitlementError =
    Boolean(initialData.entitlementCheckFailed) &&
    initialData.productInfo.entitlement === 'preview' &&
    !initialData.previewMode &&
    !entitlementErrorDismissed;
  const [pendingPurchase, setPendingPurchase] = useState<{
    quantity: number;
  } | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const hasTracked = useRef(false);
  const isTracking = useRef(false);

  const integrateReviewsData = useCallback(
    (reviewsData: ReviewsData | undefined) => {
      if (reviewsData?.reviews && Array.isArray(reviewsData.reviews)) {
        setData((prev) => {
          if (!prev.tabs?.reviews) return prev;

          const isMultiPackage = prev.productInfo.type === 'multi-package';

          const transformedReviews = reviewsData.reviews.map((review) => ({
            ...review,
            reviewDetails: {
              ...review.reviewDetails,
              canSubmitReview: true,
              ownedDate: prev.ownedDate || '',
              isMultiPackage,
              canReviewWithoutOwnership:
                prev.tabs.reviews?.canReviewWithoutOwnership,
              writeReviewModal: {
                ...(review.reviewDetails.writeReviewModal ||
                  prev.tabs?.reviews?.writeReviewModal ||
                  {}),
                productId: prev.productId,
                title: prev.tabs?.reviews?.writeReviewModal?.title || '',
                imageUrl: prev.tabs?.reviews?.writeReviewModal?.imageUrl || '',
                version:
                  review.reviewDetails.writeReviewModal?.version ||
                  prev.tabs?.reviews?.writeReviewModal?.version ||
                  '',
                ownedDate:
                  prev.tabs?.reviews?.writeReviewModal?.ownedDate ||
                  prev.ownedDate ||
                  '',
                isMultiPackage,
              } as WriteReviewModalPropsData,
            },
          }));

          return {
            ...prev,
            tabs: {
              ...prev.tabs,
              reviews: {
                ...prev.tabs.reviews,
                ...reviewsData,
                reviews: transformedReviews,
                activeFilterValue:
                  reviewsData.activeFilterValue as ProductReviewFilterValues,
                writeReviewModal: prev.tabs.reviews.writeReviewModal,
              },
            },
          };
        });
      }
    },
    []
  );

  const {
    reviewsData,
    onFilter: reviewsOnFilter,
    onSort: reviewsOnSort,
    onPageChange: reviewsOnPageChange,
    onSubmitReport: reviewsOnSubmitReport,
    onSubmitReview: reviewsOnSubmitReview,
    onDeleteReview: reviewsOnDeleteReview,
    onEditReview: reviewsOnEditReview,
    onEmailPublisher: reviewsOnEmailPublisher,
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
    fetchReviewStats: reviewsFetchStats,
  } = useReviews({
    productId: data.productId,
    publisherSupportContact: data.nutritionLabels.publisher.supportContact,
    isMultiPackage: data.productInfo.type === 'multi-package',
    initialReviewsData: data.tabs?.reviews,
  });

  const fetchAndUpdateReviewStats = useCallback(
    async (productId: string) => {
      try {
        const stats = await reviewsFetchStats(productId);
        setData((prev) => {
          const reviewList = Object.entries(stats.ratingBreakdown).map(
            ([rating, count]) => ({
              value: Number(rating),
              review: count as number,
              max: MAX_REVIEW_STARS,
            })
          );

          const roundedRating = Math.round(stats.averageRating * 10) / 10;

          const latestFiveStarReview = prev.tabs.reviews?.reviews
            ?.filter(
              (review) => review.reviewDetails.stars.value === MAX_REVIEW_STARS
            )
            .sort(
              (a, b) =>
                new Date(b.reviewDetails.publishedDate).getTime() -
                new Date(a.reviewDetails.publishedDate).getTime()
            )[0];

          return {
            ...prev,
            productInfo: {
              ...prev.productInfo,
              reviews: prev.productInfo.reviews
                ? {
                    ...prev.productInfo.reviews,
                    value: roundedRating,
                    reviews: stats.totalReviews,
                  }
                : undefined,
            },
            nutritionLabels: {
              ...prev.nutritionLabels,
              latestUserReview: latestFiveStarReview
                ? {
                    ...prev.nutritionLabels.latestUserReview,
                    stars: {
                      value: latestFiveStarReview.reviewDetails.stars.value,
                      max: MAX_REVIEW_STARS,
                    },
                    gifted: latestFiveStarReview.reviewDetails.gifted ?? false,
                    review: latestFiveStarReview.reviewDetails.review,
                    title: latestFiveStarReview.reviewDetails.title,
                  }
                : undefined,
            },
            tabs: {
              ...prev.tabs,
              reviews: prev.tabs.reviews
                ? {
                    ...prev.tabs.reviews,
                    reviewStars: {
                      ...prev.tabs.reviews.reviewStars,
                      value: roundedRating,
                      reviews: stats.totalReviews,
                    },
                    reviewList,
                  }
                : undefined,
            },
          };
        });
      } catch (error) {
        console.error('Failed to update review stats:', error);
      }
    },
    [reviewsFetchStats]
  );

  useEffect(() => {
    if (reviewsData) {
      integrateReviewsData(reviewsData);
    }
  }, [reviewsData, integrateReviewsData]);

  const scrollToTab = useCallback(() => {
    const productTabs = document.getElementById('product-tabs');

    if (productTabs) {
      productTabs.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const getTabFromHash = useCallback((): ProductTabValue => {
    const hash = window.location.hash.replace('#', '');
    const validTabs: ProductTabValue[] = [
      'overview',
      'packages',
      'releases',
      'reviews',
    ];

    return validTabs.includes(hash as ProductTabValue)
      ? (hash as ProductTabValue)
      : 'overview';
  }, []);

  useEffect(() => {
    if (shouldScrollToTab) {
      const timeoutId = setTimeout(() => {
        scrollToTab();
        setShouldScrollToTab(false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [data.currentTab, shouldScrollToTab, scrollToTab]);

  useEffect(() => {
    const initialHash = getTabFromHash();
    if (initialHash !== 'overview') {
      setData((prev) => ({ ...prev, currentTab: initialHash }));
      setShouldScrollToTab(true);
    }

    const handleHashChange = () => {
      const newTab = getTabFromHash();

      setData((prev) => ({ ...prev, currentTab: newTab }));
      setShouldScrollToTab(true);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [getTabFromHash]);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      gallery: {
        ...prev.gallery,
        hasCookieConsent,
      },
    }));
  }, [hasCookieConsent]);

  // Extract variant to avoid complex dependency
  const firstPackageVersion =
    data.tabs.packages.packages[0]?.info.latestVersion;

  useEffect(() => {
    if (
      !hasTracked.current &&
      !isTracking.current &&
      !data.previewMode &&
      data.productId &&
      data.productInfo?.productTitle
    ) {
      isTracking.current = true;
      let analyticsRetryCount = 0;

      const checkAnalyticsAndTrack = () => {
        if (window.isAnalyticsInitialized) {
          const price =
            !data.productInfo.price ||
            Object.keys(data.productInfo.price).length === 0
              ? '0.00'
              : data.productInfo.price.currentPrice;

          analytics.pushProductDetailsEvent({
            brand: data.productInfo.publisherName,
            category: data.productInfo.type,
            id: data.productId,
            name: data.productInfo.productTitle,
            price,
            variant: firstPackageVersion,
          });

          analytics.pushProductsImpressionEvent([
            {
              brand: data.productInfo.publisherName,
              category: data.productInfo.type,
              id: data.productId,
              name: data.productInfo.productTitle,
              position: 1,
              price,
              variant: firstPackageVersion,
            },
          ]);

          hasTracked.current = true;
          isTracking.current = false;
        } else if (analyticsRetryCount < MAX_ANALYTICS_RETRIES) {
          analyticsRetryCount++;
          setTimeout(checkAnalyticsAndTrack, ANALYTICS_POLLING_INTERVAL_MS);
        } else {
          console.warn('Analytics did not initialize after maximum retries.');
          isTracking.current = false;
        }
      };

      checkAnalyticsAndTrack();
    }
  }, [
    data.previewMode,
    data.productId,
    data.productInfo?.productTitle,
    data.productInfo?.publisherName,
    data.productInfo?.type,
    data.productInfo?.price,
    firstPackageVersion,
  ]);

  useEffect(() => {
    if (isProductListsEnabled && !data.previewMode) {
      fetchProductMembership(data.productId);
    }
  }, [
    isProductListsEnabled,
    data.previewMode,
    data.productId,
    fetchProductMembership,
  ]);

  // These identifiers come from SSR via initialData and are immutable for the
  // lifetime of this hook. Reading them from initialData (instead of `data`)
  // avoids stale-closure footguns when `setData` replaces the data object from
  // unrelated updates (reviews, etc.).
  const {
    productSku,
    variantSku,
    subscriptionId,
    productVariantKey,
    entitledOrganizationId,
    entitlementDetails,
    productId,
    locale,
  } = initialData;

  // orgId is passed in (not closed over) so the caller can hand in the
  // guard-narrowed org: the outer organizationId is string | undefined, but
  // the tightened builder requires string.
  const buildCheckoutUrl = useCallback(
    (orgId: string) =>
      buildCheckoutUrlPure({ env, locale, organizationId: orgId, store }),
    [env, locale, store]
  );

  useEffect(() => {
    if (!pendingPurchase || !cart?.id) return;
    if (!variantSku || !productSku) {
      Sentry.captureException(
        new Error('Buy Now reached without a product or variant SKU'),
        { extra: { productId } }
      );
      setShowServerError(true);
      setPendingPurchase(null);
      setIsPurchasing(false);
      return;
    }
    setPendingPurchase(null);
    replaceLineItemsInCart(
      [
        {
          productKey: productSku,
          variantKey: variantSku,
          quantity: pendingPurchase.quantity,
          // The active org already owns this asset only on the
          // entitled-current-org Buy Now path (an admin buying more seats from
          // the unassigned-seat state), so that's add-seats. Every other Buy
          // Now path — not-entitled, or entitled-another-org where the active
          // org does not own it — is a fresh purchase. Checkout reads
          // lineItemType to handle can-subscribe errors correctly.
          lineItemType:
            data.productInfo.entitlement === 'entitled-current-org'
              ? LineItemType['add-seats']
              : LineItemType.purchase,
        },
      ],
      {
        onSuccess: ({ success, cart: updatedCart }) => {
          const targetCartId = updatedCart?.id ?? cart.id;
          if (!success || !targetCartId) {
            Sentry.captureException(
              new Error('Cart replace did not return a cart on Buy Now'),
              { extra: { productId } }
            );
            setShowServerError(true);
            setIsPurchasing(false);
            return;
          }
          // Checkout resolves the cart from organizationId; without it the
          // redirect lands on an org-less cart checkout cannot identify.
          if (!organizationId) {
            Sentry.captureException(
              new Error('organizationId missing on Buy Now checkout redirect'),
              { extra: { productId } }
            );
            setShowServerError(true);
            setIsPurchasing(false);
            return;
          }
          window.location.assign(buildCheckoutUrl(organizationId));
        },
        onError: (error) => {
          Sentry.captureException(error);
          console.error('Failed to start purchase flow', error);
          setShowServerError(true);
          setIsPurchasing(false);
        },
      }
    );
  }, [
    pendingPurchase,
    cart?.id,
    productSku,
    variantSku,
    productId,
    organizationId,
    replaceLineItemsInCart,
    buildCheckoutUrl,
    data.productInfo.entitlement,
  ]);

  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  const redirectToSignIn = useCallback(async () => {
    const callbackUrl = window.location.pathname + window.location.search;
    await signIn(GENESIS_PROVIDER_ID, { callbackUrl });
  }, []);

  const purchaseProduct = useCallback(
    (quantity: number) => {
      analytics.pushBuyNowEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });
      if (!userId) {
        redirectToSignIn();
        return;
      }
      setShowServerError(false);
      setIsPurchasing(true);
      // Both of these issue bridge calls — enabling the fetch, and arming the
      // effect that replaces line items on an already-fetched cart — so
      // neither may happen before X-Authorization has something to carry.
      ensureBridgeToken().then((token) => {
        if (token) {
          setPendingPurchase({ quantity });
          setEnableCartFetching(true);
          return;
        }
        Sentry.captureException(
          new Error('Buy Now: no bridge token could be provisioned'),
          { extra: { productId } }
        );
        setIsPurchasing(false);
        setShowServerError(true);
      });
    },
    [
      userId,
      redirectToSignIn,
      setEnableCartFetching,
      ensureBridgeToken,
      productId,
      data.productId,
      data.productInfo.productTitle,
    ]
  );

  const applyEntitledState = useCallback(
    ({ justClaimed = true }: { justClaimed?: boolean } = {}) => {
      setData((prev) => ({
        ...prev,
        ...(justClaimed && {
          wasEntitled: true,
          ownedDate: formatDate(new Date(), 'PP', prev.locale),
        }),
        productInfo: {
          ...prev.productInfo,
          entitlement: 'entitled-current-org',
        },
        tabs: {
          ...prev.tabs,
          packages: {
            ...prev.tabs.packages,
            packages: prev.tabs.packages.packages.map((pck) => ({
              ...pck,
              isEntitled: true,
            })),
          },
        },
      }));
    },
    []
  );

  // A Back navigation can restore the page from bfcache with the CTA still
  // locked for a pending sign-in redirect; unlock it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsClaiming(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const claimProduct = useCallback(async () => {
    if (isClaiming) {
      return;
    }
    if (!userId) {
      redirectToSignIn();
      return;
    }
    setShowServerError(false);
    setIsClaiming(true);
    let redirectingToSignIn = false;
    try {
      await failingFetcher('/api/claim-product', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
        }),
      });

      analytics.pushAddToMyAssetEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });

      applyEntitledState();
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        // Keep the CTA disabled while the sign-in redirect is pending.
        redirectingToSignIn = true;
        redirectToSignIn().catch(() => setIsClaiming(false));
        return;
      }
      // 409 means the org already owns the product; show ownership, not an error.
      if (error instanceof FetchError && error.status === 409) {
        applyEntitledState({ justClaimed: false });
        return;
      }
      console.error('Error claiming product:', error);
      setShowServerError(true);
    } finally {
      if (!redirectingToSignIn) {
        setIsClaiming(false);
      }
    }
  }, [
    isClaiming,
    userId,
    redirectToSignIn,
    applyEntitledState,
    data.productId,
    data.productInfo.productTitle,
  ]);

  const updateCanSubmitReview = useCallback((canSubmitReview: boolean) => {
    setData((prev) => ({
      ...prev,
      canSubmitReview,
      purchasedItem: prev.purchasedItem
        ? {
            ...prev.purchasedItem,
            canWriteReview: canSubmitReview,
          }
        : prev.purchasedItem,
      tabs: {
        ...prev.tabs,
        reviews: prev.tabs.reviews
          ? {
              ...prev.tabs.reviews,
              canWriteReview: canSubmitReview,
            }
          : undefined,
      },
    }));
  }, []);

  const navigateToReviews = useCallback(() => {
    if (breakpoints?.isMobile) {
      setData((prev) => ({
        ...prev,
        activeSheetIndex: prev.tabs.reviews ? REVIEWS_SHEET_INDEX : null,
      }));
    } else {
      setData((prev) => ({
        ...prev,
        currentTab: 'reviews' as ProductTabValue,
      }));
    }

    setShouldScrollToTab(true);

    if (window.location.hash !== '#reviews') {
      window.location.hash = 'reviews';
    }
  }, [breakpoints?.isMobile]);

  const openPackageInUnity = useCallback(
    (upmPackage: PackageListDetailPropsData) => {
      analytics.pushOpenInUnityEvent({
        asset_id: upmPackage.id,
        asset_name: data.productInfo.productTitle,
      });
      openInUnityEditor({
        id: upmPackage.id,
        packageName: upmPackage.info.packageName,
        isSourceUpmRegistry: upmPackage.isSourceUpmRegistry,
      });
    },
    [data.productInfo.productTitle]
  );

  const openFirstPackageInUnity = useCallback(() => {
    openPackageInUnity(data.tabs.packages.packages[0]);
  }, [data.tabs.packages.packages, openPackageInUnity]);

  // The Organization Members page is org-scoped; without an org the dashboard
  // can't resolve the page, so report and skip rather than opening a broken
  // page (mirrors the Manage seats guard).
  const requestSeat = useCallback(() => {
    analytics.pushRequestSeatEvent({
      asset_id: data.productId,
      asset_name: data.productInfo.productTitle,
    });
    if (!organizationId) {
      Sentry.captureException(
        new Error('organizationId missing on Request seat redirect'),
        { extra: { productId: data.productId } }
      );
      return;
    }
    openRequestSeatUrl(buildRequestSeatUrl({ env, organizationId }));
  }, [env, organizationId, data.productId, data.productInfo.productTitle]);

  // A priced, unowned product hides its CTA until paid-UPM ships; the component
  // hides the CTA when entitlement is 'preview' (see the return mapping below).
  // Switching org would not help there, so the region notice is suppressed too.
  const hidePurchaseCta =
    Boolean(data.productInfo.price.originalPrice) &&
    !isPaidUpmEnabled &&
    (data.productInfo.entitlement === 'not-entitled' ||
      data.productInfo.entitlement === 'region-restricted');

  const actions: UpmProductDetailPagePropsActions = {
    onAddToMyAssets: claimProduct,
    onBuyNow: purchaseProduct,
    onRequestSeat: requestSeat,
    onOpenInUnity: openFirstPackageInUnity,

    // Secondary CTA for paid owned assets. My Products is org-scoped; without an
    // org the dashboard can't resolve the page, so report and skip rather than
    // opening a useless home tab (mirrors the Buy Now checkout guard).
    onManageSeats: useCallback(() => {
      analytics.pushManageSeatsEvent({
        asset_id: data.productId,
        asset_name: data.productInfo.productTitle,
      });
      if (!organizationId) {
        Sentry.captureException(
          new Error('organizationId missing on Manage seats redirect'),
          { extra: { productId: data.productId } }
        );
        return;
      }
      // The active org's own entitlement is the only one whose subscription its
      // org path can resolve, so take it from the per-org `entitlements`
      // expansion. Backends predating that expansion (and hard-failed
      // expansions) send only the singular one, which describes an arbitrary
      // entitled org — usable just when that org happens to be the active one.
      // With neither, the org-level list is the safe target: pairing one org's
      // path with another's subscription points at inventory it can't resolve.
      const activeOrgEntitlement = entitlementDetails?.find(
        (entitlement) => entitlement.organizationId === organizationId
      );
      const deepLink =
        activeOrgEntitlement ??
        (organizationId === entitledOrganizationId
          ? { subscriptionId, productVariantKey }
          : undefined);
      openManageSeatUrl(
        buildManageSeatUrl({
          env,
          organizationId,
          subscriptionId: deepLink?.subscriptionId,
          productVariantKey: deepLink?.productVariantKey,
        })
      );
    }, [
      env,
      organizationId,
      subscriptionId,
      productVariantKey,
      entitledOrganizationId,
      entitlementDetails,
      data.productId,
      data.productInfo.productTitle,
    ]),

    onOpenInUnityFromPackage: openPackageInUnity,

    onOpenInUnityFromEntitlement: openFirstPackageInUnity,

    onGoToMyAssets: useCallback(() => {
      router.push('/account/assets/upm');
    }, [router]),

    onGoToHomepage: useCallback(() => {
      router.push('/');
    }, [router]),

    onCloseFromEntitlement: useCallback(() => {
      setData((prev) => ({ ...prev, wasEntitled: false }));
    }, []),

    onSeeOwnedPackages: useCallback(() => {
      setData((prev) => ({ ...prev, currentTab: 'packages' }));
      setShouldScrollToTab(true);
    }, []),

    onPackageDetailsClick: useCallback(() => {
      setData((prev) => ({ ...prev, currentTab: 'packages' }));
      setShouldScrollToTab(true);
    }, []),

    onClickChipList: useCallback(
      (item?: string) => {
        if (!item) {
          return;
        }

        navigateToSearch(item);
      },
      [navigateToSearch]
    ),

    onHandleTabChange: useCallback((tab: ProductTabValue) => {
      setData((prev) => ({ ...prev, currentTab: tab }));

      if (tab !== 'reviews' && window.location.hash) {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
    }, []),

    onOpenCookieSettings: openCookieSettings,

    onAccordionChange: useCallback((name: string, action: 'open' | 'close') => {
      analytics.pushProductAccordionClickEvent({
        action_name: name,
        action,
      });
    }, []),

    onDismiss: useCallback(() => {
      setShowServerError(false);
      setEntitlementErrorDismissed(true);
    }, []),

    onOpenSheet: useCallback((index: number) => {
      setData((prev) => ({ ...prev, activeSheetIndex: index }));
    }, []),
    onCloseSheet: useCallback(() => {
      setData((prev) => ({ ...prev, activeSheetIndex: null }));
    }, []),

    // Reviews actions
    onDeleteReview: useCallback(
      async (reviewId: string) => {
        try {
          await reviewsOnDeleteReview(reviewId);
          setShowServerError(false);
          await fetchAndUpdateReviewStats(data.productId);
          updateCanSubmitReview(true);
        } catch (error) {
          setShowServerError(true);
          throw error;
        }
      },
      [
        reviewsOnDeleteReview,
        fetchAndUpdateReviewStats,
        data.productId,
        updateCanSubmitReview,
      ]
    ),
    onFilterReviews: useCallback(
      async (filter: number) => {
        const filterStr = filter === 0 ? '' : filter.toString();
        setShouldScrollToTab(true);
        await reviewsOnFilter(filterStr);
      },
      [reviewsOnFilter]
    ),
    onEditReview: useCallback(
      async (
        reviewId: string,
        formData: ReviewFormData,
        lastUpdatedAt: string
      ) => {
        const updatedReviewsData = await reviewsOnEditReview(
          reviewId,
          formData,
          lastUpdatedAt
        );
        if (updatedReviewsData) {
          integrateReviewsData(updatedReviewsData);
          await fetchAndUpdateReviewStats(data.productId);
        }
      },
      [
        reviewsOnEditReview,
        integrateReviewsData,
        data.productId,
        fetchAndUpdateReviewStats,
      ]
    ),
    onFilter: useCallback(
      async (filter: string) => {
        setShouldScrollToTab(true);
        await reviewsOnFilter(filter);
      },
      [reviewsOnFilter]
    ),
    onSort: useCallback(
      async (sort: string) => {
        setShouldScrollToTab(true);
        await reviewsOnSort(sort);
      },
      [reviewsOnSort]
    ),
    onPageChange: useCallback(
      async (page: number) => {
        setShouldScrollToTab(true);
        const reviewsData = await reviewsOnPageChange(page);
        return reviewsData;
      },
      [reviewsOnPageChange]
    ),
    onSubmitReport: useCallback(
      async (
        reviewId: string,
        report: { reportReview: string; reportType: string }
      ) => {
        await reviewsOnSubmitReport(reviewId, data.productId, {
          reportReview: report.reportReview,
          type: report.reportType,
        });
      },
      [reviewsOnSubmitReport, data.productId]
    ),
    onEmailPublisher: reviewsOnEmailPublisher,
    onSubmitReview: useCallback(
      async (productId: string, formData: ReviewFormData) => {
        const reviewsData = await reviewsOnSubmitReview(productId, formData);
        if (reviewsData) {
          integrateReviewsData(reviewsData);
          await fetchAndUpdateReviewStats(productId);
          updateCanSubmitReview(false);
        }
      },
      [
        reviewsOnSubmitReview,
        integrateReviewsData,
        fetchAndUpdateReviewStats,
        updateCanSubmitReview,
      ]
    ),
    onClickReadMore: navigateToReviews,

    // TODO: These features are not going to be part of the GA launch
    onHelpfulVotesReview: reviewsOnHelpfulVotesReview,
    onSubmitReply: reviewsOnSubmitReply,
    onHelpfulVotesReply: reviewsOnHelpfulVotesReply,
    onEditReply: reviewsOnEditReply,
    onDeleteReply: reviewsOnDeleteReply,
    // Favorite actions
    onFavorite:
      isProductListsEnabled && !data.previewMode
        ? () => {
            toggleProductList({
              id: data.productId,
              name: data.productInfo.productTitle,
            });
          }
        : undefined,
  };

  const isFavorite =
    isProductListsEnabled && !data.previewMode
      ? isProductInList(data.productId)
      : undefined;

  return {
    data: {
      ...data,
      productInfo: {
        ...data.productInfo,
        isFavorite,
        isPurchasing,
        isClaiming,
        entitlement: hidePurchaseCta ? 'preview' : data.productInfo.entitlement,
        // Manage seat is part of the paid-UPM experience; gate behind the flag
        // so owned assets keep Open-in-Unity until paid-UPM is live.
        isManageSeatEnabled: isPaidUpmEnabled,
      },
      showServerError: showServerError || showEntitlementError,
    },
    actions,
  };
};
